import React, { useRef, useEffect, useState } from 'react'
import { Card, CardBody, CardTitle, CardText } from 'reactstrap'
import {
  IconChevronDown,
  IconInfoCircle,
  IconLock,
  IconCheck,
  IconExternalLink,
  IconCirclePlus,
  IconMessageCircle
} from '@tabler/icons-react'
// @ts-ignore
import { Badge, Button } from 'dsm-react-lib'
import { useNavigate } from 'react-router-dom'
import { staticAssetsPath, uatUrl } from '../../../configs/constants'
import { useSasToken } from '../../../_hooks/useSasToken'
import AIReadinessGauge from './AIReadinessGauge'
import DataQualityScore from './DataQualityScore'
import { Tooltip } from 'react-tooltip'
import QuickActions from './QuickActions'
import { Collapse, CollapseHistory } from '../Chatbot/icons/Icons'
import { useDispatch, useSelector } from 'react-redux'
import { setIsChatOpen, setProducts } from '../../../_redux/actions/dataMarketPlaceActions'
import { useTrackEvent } from '@microsoft/applicationinsights-react-js'
import { appInsights } from '../../../_services/AppInsights'
import { EventType } from '../../../utility/types'
import Skeleton from 'react-loading-skeleton'

interface AssetCardProps {
  asset: {
    id: number
    name: string
    description: string
    dataCategory: string
    collection: string
    dataProviders: string
    imageSrc: string
    tier: string
    dataProductName: string
    securityClassification?: string
    accessStatus?: string
    arcRoles?: { role: string; group: string }[]
    dataProductId?: string | number
  }
  showButtons?: boolean
  className?: string
  searchTerm?: string
  aiScore?: number
  dataScore?: number
  isCollapsed?: boolean
  isReadOnlyCard?: boolean
  setIsCollapsed?: (collapsed: boolean) => void
  accessControl?: any
  onAddToDatabox?: Function
  isChatEnabled?: boolean
  requestAccess?: Function
  metadataSource?: string
  dataQualityStatement?: string
  exploreUrl?: string
  onBeforeNavigate?: () => void
}
export const renderAssetCardSkeletonLoader = (count: number = 3) => {
  return (
    <div className="flex gap-2 flex-wrap">
      {[...Array(count)].map((_: any, index: number) => (
        <Card
          key={index}
          className="theme-surface theme-card-border transition-all duration-300 pb-[4px] !w-[400px]">
          {/* Image section skeleton */}
          <div className="m-1 mt-0.5 relative w-auto h-[120px] overflow-hidden rounded-[5px]">
            <Skeleton
              height="100%"
              width="100%"
              className="!rounded-[5px]"
              baseColor="var(--skeleton-base)"
            />
          </div>

          <CardBody className="pt-0.5">
            {/* Title skeleton */}
            <CardTitle tag="h5" className="!font-bold !my-0.5 min-h-[30px]">
              <Skeleton width="80%" height={24} baseColor="var(--skeleton-base)" />
            </CardTitle>

            {/* Description skeleton */}
            <CardText className="mb-1">
              <Skeleton count={2} width="100%" baseColor="var(--skeleton-base)" />
            </CardText>

            <hr className="border-t theme-border-default my-1 w-full" />

            {/* Data product domain skeleton */}
            <div className="flex justify-between mb-1 text-sm">
              <span className="text-[var(--color-primary)] font-semibold">
                DATA PRODUCT DOMAIN:
              </span>
              <Skeleton width={120} height={20} baseColor="var(--skeleton-base)" />
            </div>

            {/* Data source skeleton */}
            <div className="flex justify-between items-start text-sm mb-1">
              <span className="text-[var(--color-primary)] font-semibold">DATA SOURCE:</span>
              <Skeleton width={150} height={20} baseColor="var(--skeleton-base)" />
            </div>
            <div>
              <Button
                className="mt-2 w-full flex justify-center items-center"
                icon={<IconMessageCircle size={12} />}
                label="Loading..."
                color="primary"
              />
            </div>
          </CardBody>
        </Card>
      ))}
    </div>
  )
}

export const convertExploreUrlToWorkspace = (url: string): string => {
  try {
    let path = url.trim()
    const absoluteUrlMatch = path.match(/^[a-zA-Z][a-zA-Z\d+.-]*:\/\/[^/]+(\/.*)?$/)

    if (absoluteUrlMatch) {
      path = absoluteUrlMatch[1] || '/'
    } else if (!path.startsWith('/')) {
      return url
    }

    const normalizedPath = encodeURI(decodeURIComponent(path))
    return `/workspace?activeApp=Smart-Data-Hub&redirectUrl=${normalizedPath}`
  } catch {
    return url
  }
}

const AssetCard: React.FC<AssetCardProps> = ({
  asset,
  showButtons = true,
  className = '',
  searchTerm,
  aiScore,
  dataScore,
  isCollapsed = false,
  isReadOnlyCard = false,
  setIsCollapsed,
  accessControl,
  onAddToDatabox,
  isChatEnabled,
  requestAccess,
  metadataSource,
  dataQualityStatement,
  exploreUrl,
  onBeforeNavigate
}) => {
  // Track a key to force remount of GaugeChart on expand
  const [gaugeKey, setGaugeKey] = useState(0)
  const [isAccessMenuOpen, setIsAccessMenuOpen] = useState(false)
  const accessMenuRef = useRef<HTMLDivElement>(null)
  const prevCollapsed = useRef(isCollapsed)
  const navigate = useNavigate()
  const { appendToken } = useSasToken()
  const imgPath = `${staticAssetsPath}/images/dataMarketplace`
  // show demo feature only on uat
  const isUAT = window.location.origin === uatUrl

  const { products, isChatOpen } = useSelector((state: any) => state.dataMarketPlaceReducer)
  const dispatch = useDispatch()
  // Check if this specific asset's chat is open
  const isThisAssetChatOpen =
    isChatOpen &&
    products &&
    products?.some((p: any) => p.label === asset?.name || p.label === asset?.dataProductName)

  //@ts-ignore
  const trackAIChat = useTrackEvent(appInsights, EventType.DMAIChat)
  //@ts-ignore
  const trackExplore = useTrackEvent(appInsights, EventType.DMDataHub)
  //@ts-ignore
  const trackViewDetails = useTrackEvent(appInsights, EventType.DMViewDetails)
  //@ts-ignore
  const trackRequestAccess = useTrackEvent(appInsights, EventType.DMRequestAccess)
  //@ts-ignore
  const trackAddToDataboxClicked = useTrackEvent(appInsights, EventType.DMAddToDataboxClicked)

  const handleChatToggle = (asset: any) => {
    if (isThisAssetChatOpen) {
      // Remove this asset from the products list
      const filteredProducts = products.filter(
        (p: any) => p.label !== asset?.name && p.label !== asset?.dataProductName
      )
      dispatch(setProducts(filteredProducts))
      // Optionally close chat if no products left
      if (filteredProducts.length === 0) {
        dispatch(setIsChatOpen(false))
      }
    } else {
      // Open chat for this asset
      dispatch(setIsChatOpen(true))
      trackAIChat({
        dataProductName: asset?.name || asset?.dataProductName,
        dataProductId: asset?.id || asset?.dataProductId
      })
      dispatch(
        setProducts([
          // ...(products || []), // to make single select
          {
            label: asset?.name || asset?.dataProductName,
            value: asset?.id || asset?.dataProductId
          }
        ])
      )
    }
  }

  const hasAccess = asset?.accessStatus
    ? asset.accessStatus === 'FULL_ACCESS'
    : accessControl
    ? !!accessControl?.hasAccess
    : true

  useEffect(() => {
    if (!isAccessMenuOpen) return
    const handleClickOutside = (e: MouseEvent) => {
      if (accessMenuRef.current && !accessMenuRef.current.contains(e.target as Node)) {
        setIsAccessMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isAccessMenuOpen])

  useEffect(() => {
    if (prevCollapsed.current && !isCollapsed) {
      setGaugeKey((k) => k + 1)
    }
    prevCollapsed.current = isCollapsed
  }, [isCollapsed])

  // Function to handle navigation to the detail page
  const handleViewDetails = (id: number) => {
    if (onBeforeNavigate) {
      onBeforeNavigate()
    }
    trackViewDetails({ dataProductName: asset?.name || asset?.dataProductName, dataProductId: id })
    navigate(`/data-marketplace/${id}`)
  }

  //highlightText function somewhere above in your component
  const highlightText = (text: string, searchTerm: string | undefined) => {
    if (!searchTerm) return text
    const regex = new RegExp(`(${searchTerm})`, 'gi')
    const parts = text.split(regex)
    return parts.map((part, index) =>
      regex.test(part) ? (
        <span key={index} className="bg-yellow-300">
          {part}
        </span>
      ) : (
        part
      )
    )
  }

  return (
    <Card
      data-testid="asset-card"
      className={`${
        showButtons ? 'theme-surface theme-card-border' : 'theme-overlay-card'
      } !rounded-[8px] transition-all duration-300 flex flex-col ${
        isCollapsed ? '!w-[60px] overflow-hidden h-[1000px]' : '!w-[380px]'
      } ${className}`}>
      {!showButtons && (
        <div
          className={`flex ${
            isCollapsed ? 'justify-center' : 'justify-between'
          } items-center mx-1`}>
          {!isCollapsed && <h4 className="font-bold">Overview</h4>}
          {isCollapsed ? (
            <div
              id="closeSidebar"
              className="flex justify-end p-1 mx-50 cursor-pointer transition-colors duration-150 group">
              <Collapse
                className="p-0 [&>path]:transition-colors [&>path]:duration-150 group-hover:[&>path]:stroke-[var(--color-primary)]"
                size={30}
                data-testid="openSidebar"
                data-tooltip-id="openSidebar"
                onClick={() => {
                  setIsCollapsed && setIsCollapsed(!isCollapsed)
                }}
              />
            </div>
          ) : (
            <div
              id="closeSidebar"
              className="flex justify-end py-1 mx-50 cursor-pointer transition-colors duration-150 group">
              <CollapseHistory
                className="p-0 text-[var(--gray-500)] [&>path]:transition-colors [&>path]:duration-150 group-hover:[&>path]:stroke-[var(--color-primary)]"
                size={30}
                data-testid="closeSidebar"
                data-tooltip-id="closeSidebar"
                onClick={() => {
                  setIsCollapsed && setIsCollapsed(!isCollapsed)
                }}
              />
            </div>
          )}
        </div>
      )}
      {!isCollapsed && (
        <>
          <div className={`${showButtons ? 'px-[24px] pt-[24px]' : 'm-1 mt-0.5'} `}>
            <div
              className={`relative overflow-hidden ${
                showButtons ? 'h-[100px] w-full rounded-[2px]' : 'w-auto h-[140px]'
              }`}>
              <span className="shine-effect" />
              <div className="absolute inset-0 [clip-path:polygon(0_0,60%_0,75%_100%,0_100%)]">
                <img
                  src={appendToken(`${imgPath}/${asset?.name || asset.dataProductName}.jpg`)}
                  alt="assets"
                  className={`${
                    showButtons ? 'w-full' : 'w-3/4'
                  } h-full object-cover rounded-[5px]`}
                  onError={(e) => {
                    e.currentTarget.onerror = null
                    e.currentTarget.src = appendToken(`${imgPath}/data-product.jpg`)
                  }}
                />
              </div>

              <div className="absolute inset-0 pointer-events-none theme-surface z-10 [clip-path:polygon(60%_0,60.04%_0,75.04%_100%,75%_100%)]" />

              <div className="absolute inset-0 [clip-path:polygon(61%_0,100%_0,100%_100%,76%_100%)]">
                <div
                  className={`w-full h-full rounded-[5px] ${
                    asset.tier === 'Business Optimised'
                      ? 'bg-[var(--tier-optimised)]'
                      : asset.tier === 'Curated'
                      ? 'bg-[var(--tier-curated)]'
                      : 'bg-[var(--tier-standard)]'
                  }`}
                />
                <div className="absolute top-[16px] right-[10px] w-[100px] text-right">
                  <span className="text-[10px] leading-[14px] tracking-[0.2px] opacity-60 uppercase text-white inline-flex items-center justify-end gap-[2px]">
                    TIER
                    <IconInfoCircle
                      size={12}
                      className="cursor-pointer"
                      stroke="currentColor"
                      data-tooltip-id={`tier-${asset.tier}`}
                      data-testid="info-icon"
                    />
                  </span>
                  <span className="block text-white text-sm font-extrabold break-words whitespace-pre-line ">
                    {asset.tier === 'Business Optimised'
                      ? 'BUSINESS\nOPTIMISED'
                      : asset.tier === 'Curated'
                      ? 'CURATED'
                      : 'SOURCE'}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <Tooltip
            id={`tier-${asset.tier}`}
            place="left"
            className="rounded-lg px-4 py-2 max-w-xs text-center !z-[9999]">
            {asset.tier === 'Business Optimised'
              ? 'Data products specifically designed to support business areas and processes'
              : asset.tier === 'Curated'
              ? 'Standardised data, aligned to well defined business domains, consolidated from multiple sources'
              : 'Raw or minimally processed data for early exploration'}
          </Tooltip>
          <CardBody
            className={`${showButtons ? '!p-[24px] pt-[16px]' : 'pt-0.5'} flex flex-col flex-1`}>
            {showButtons && (
              <>
                <div className="flex items-center gap-25">
                  <CardTitle
                    tag="h5"
                    data-testid="asset-card-title"
                    className="!mb-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-[20px] leading-[28px] font-bold theme-text-heading">
                    {highlightText(asset?.name || asset.dataProductName || '', searchTerm)}
                  </CardTitle>
                </div>
                <div className="h-[78px]">
                  <CardText
                    data-testid="asset-card-description"
                    className="line-clamp-3 text-[14px] leading-[22px] font-normal theme-text-body mb-[12px]"
                    title={asset.description}>
                    {asset.description}
                  </CardText>
                </div>
                <hr className="border-t border-[var(--divider-color)] my-1 w-full" />
              </>
            )}
            <div
              className={`flex ${
                showButtons && !isReadOnlyCard
                  ? 'gap-[16px]'
                  : isReadOnlyCard
                  ? 'flex-col gap-[4px]'
                  : 'justify-between'
              } mb-1 text-sm ${showButtons && !isReadOnlyCard ? 'h-[96px]' : ''}`}>
              <div
                className={`${showButtons && !isReadOnlyCard ? 'w-[148px]' : ''} ${
                  isReadOnlyCard ? 'flex justify-between items-center' : ''
                }`}>
                <div className="text-[10px] tracking-[0.2px] uppercase font-semibold theme-text-heading">
                  {showButtons && !isReadOnlyCard ? 'DOMAIN' : 'DATA PRODUCT DOMAIN:'}
                </div>
                <span
                  className={`${
                    showButtons && !isReadOnlyCard
                      ? 'text-[16px] leading-[24px] font-semibold theme-text-body'
                      : 'theme-text-heading font-semibold'
                  }`}
                  title={asset?.dataCategory || '-'}
                  data-testid="asset-card-domain-value">
                  {asset?.dataCategory || '-'}
                </span>
              </div>
              {!showButtons && !isReadOnlyCard && <span />}
              <div
                className={`${showButtons && !isReadOnlyCard ? 'w-[148px]' : ''} ${
                  isReadOnlyCard
                    ? 'flex justify-between items-center'
                    : showButtons
                    ? ''
                    : 'text-right'
                }`}>
                <div className="text-[10px] tracking-[0.2px] uppercase font-semibold theme-text-heading">
                  {showButtons && !isReadOnlyCard ? 'SOURCE' : 'DATA SOURCE:'}
                </div>
                <span
                  className={`${
                    showButtons && !isReadOnlyCard
                      ? 'text-[16px] leading-[24px] font-semibold theme-text-body line-clamp-3'
                      : 'theme-text-heading font-semibold'
                  }`}
                  title={asset?.dataProviders || '-'}
                  data-testid="asset-card-source-value">
                  {asset?.dataProviders || '-'}
                </span>
              </div>
            </div>

            {showButtons && !isReadOnlyCard && (
              <div className="mb-[12px]">
                <div className="text-[10px] tracking-[0.2px] uppercase font-semibold theme-text-heading">
                  ACCESS
                </div>
                <div className="h-[40px] flex items-center py-[6px]">
                  {accessControl?.isLoading ? (
                    <Skeleton
                      width={80}
                      height={24}
                      baseColor="var(--skeleton-base)"
                      className="!rounded-[15px]"
                    />
                  ) : accessControl?.isPending ? (
                    <Badge
                      data-testid="asset-card-access-badge"
                      className="app-status-badge"
                      color="warning"
                      variant="light"
                      label="Pending"
                      type="pill"
                    />
                  ) : (
                    <span
                      data-testid="asset-card-access-badge"
                      className={`rounded-[15px] h-6 inline-flex items-center justify-center text-[12px] leading-[18px] font-medium px-1 ${
                        hasAccess ? 'theme-badge-primary-soft' : 'theme-badge-danger'
                      }`}>
                      {hasAccess ? 'Full access' : 'No access'}
                    </span>
                  )}
                </div>
              </div>
            )}

            {showButtons && !isReadOnlyCard ? (
              <div className="flex gap-[8px] w-full mt-auto">
                <Button
                  color="primary"
                  variant="fill"
                  label="View details"
                  className="flex-1 justify-center px-3 !rounded-[2px] h-[40px] whitespace-nowrap"
                  onClick={() => handleViewDetails(asset?.id)}
                  data-testid="asset-card-view-details-btn"
                />

                {/* Access / Request button */}
                {hasAccess && (exploreUrl || isChatEnabled) ? (
                  <div className="relative flex-1" ref={accessMenuRef}>
                    <button
                      type="button"
                      className="theme-accent-outline-btn flex items-center justify-center gap-[6px] w-full h-[40px] px-[10px] rounded-[2px] text-[14px] font-medium tracking-[0.07px] cursor-pointer"
                      aria-label="Access"
                      data-testid="asset-card-access-btn"
                      onClick={() => {
                        setIsAccessMenuOpen((prev) => !prev)
                      }}>
                      Access
                      <IconChevronDown size={14} />
                    </button>
                    {isAccessMenuOpen && (
                      <div
                        className="absolute left-full bottom-0 ml-[4px] z-20 min-w-[150px] rounded-[8px] theme-surface-muted shadow-[var(--content-card-shadow)] overflow-hidden py-[4px]"
                        data-testid="asset-card-access-menu">
                        {exploreUrl && (
                          <button
                            type="button"
                            className="theme-interactive-item theme-text-heading flex items-center gap-[6px] w-full text-left px-[10px] h-[28px] text-[12px] leading-[16px] font-medium bg-transparent border-0 cursor-pointer"
                            aria-label="Explore"
                            data-testid="asset-card-explore-btn"
                            onClick={() => {
                              trackExplore({ dataProductName: asset?.dataProductName })
                              setIsAccessMenuOpen(false)
                              setTimeout(() => {
                                navigate(convertExploreUrlToWorkspace(exploreUrl))
                              }, 10)
                            }}>
                            <IconExternalLink size={14} className="shrink-0" />
                            Explore
                          </button>
                        )}
                        {isChatEnabled && (
                          <button
                            type="button"
                            className="theme-interactive-item theme-text-heading flex items-center gap-[6px] w-full text-left px-[10px] h-[28px] text-[12px] leading-[16px] font-medium bg-transparent border-0 cursor-pointer"
                            aria-label={isThisAssetChatOpen ? 'Added to chatbox' : 'Add to chatbox'}
                            data-testid="asset-card-chat-btn"
                            onClick={() => {
                              handleChatToggle(asset)
                              setIsAccessMenuOpen(false)
                            }}>
                            <IconMessageCircle size={14} className="shrink-0" />
                            {isThisAssetChatOpen ? 'Added to chatbox' : 'Add to chatbox'}
                          </button>
                        )}
                        {isChatEnabled && (
                          <button
                            type="button"
                            className="theme-interactive-item theme-text-heading flex items-center gap-[6px] w-full text-left px-[10px] h-[28px] text-[12px] leading-[16px] font-medium bg-transparent border-0 cursor-pointer"
                            aria-label="Add to databox"
                            data-testid="asset-card-add-to-databox-btn"
                            onClick={() => {
                              setIsAccessMenuOpen(false)
                              trackAddToDataboxClicked({
                                dataProductName: asset?.name || asset?.dataProductName,
                                dataProductId: asset?.id
                              })
                              onAddToDatabox && onAddToDatabox()
                            }}>
                            <IconCirclePlus size={14} className="shrink-0" />
                            Add to databox
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ) : !hasAccess ? (
                  <div className="flex-1">
                    <Button
                      color={accessControl?.isPending ? 'warning' : 'primary'}
                      variant="outline"
                      label={
                        accessControl?.isLoading
                          ? 'Loading...'
                          : accessControl?.isPending
                          ? 'Pending Request'
                          : 'Request access'
                      }
                      icon={<IconLock size={14} />}
                      iconOnly={false}
                      disabled={accessControl?.isLoading || accessControl?.isPending}
                      className={`w-full justify-center h-[40px] !rounded-[2px] whitespace-nowrap ${
                        accessControl?.isPending ? 'theme-warning-light-btn' : 'dsm-brand-accent'
                      }`}
                      onClick={() => requestAccess?.()}
                      data-testid="DMRequestAccessBtnCard"
                    />
                  </div>
                ) : null}
              </div>
            ) : showButtons && isReadOnlyCard ? (
              <div className="mt-auto">
                <Button
                  color="primary"
                  variant="fill"
                  label="View details"
                  icon={<IconExternalLink size={14} />}
                  iconOnly={false}
                  className="w-full justify-center h-[40px] !rounded-[2px]"
                  onClick={() => handleViewDetails(asset?.id)}
                  data-testid="asset-card-view-details-btn"
                />
              </div>
            ) : !isReadOnlyCard ? (
              <div>
                {isChatEnabled ? (
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      color="primary"
                      label="Add to databox"
                      disabled={accessControl?.isLoading || !accessControl?.hasAccess}
                      onClick={() => {
                        trackAddToDataboxClicked({
                          dataProductName: asset?.name || asset?.dataProductName,
                          dataProductId: asset?.id
                        })
                        onAddToDatabox && onAddToDatabox()
                      }}
                      iconOnly={false}
                      icon={
                        accessControl?.hasAccess ? (
                          <IconCirclePlus size={18} />
                        ) : (
                          <IconLock size={18} />
                        )
                      }
                      className="flex-1 justify-center p-1 dsm-brand-accent"
                      size="small"
                      data-testid="asset-sidebar-add-to-databox-btn"
                    />
                    <Button
                      variant="fill"
                      color="secondary"
                      disabled={accessControl?.isLoading || !accessControl?.hasAccess}
                      label={isThisAssetChatOpen ? 'Added to chat' : 'Add to chat'}
                      iconOnly={false}
                      icon={
                        accessControl?.hasAccess ? (
                          isThisAssetChatOpen ? (
                            <IconCheck size={18} />
                          ) : (
                            <IconMessageCircle size={18} />
                          )
                        ) : (
                          <IconLock size={18} />
                        )
                      }
                      className="flex-1 justify-center p-1"
                      onClick={() => handleChatToggle(asset)}
                      size="small"
                      data-testid="asset-sidebar-add-to-chat-btn"
                    />
                  </div>
                ) : (
                  <></>
                )}
                {['cat', 'idh'].includes(metadataSource?.toLowerCase() || '') && exploreUrl && (
                  <div className="flex mt-1 space-x-2 ">
                    <Button
                      color="primary"
                      label="Explore"
                      disabled={accessControl?.isLoading || !accessControl?.hasAccess}
                      onClick={() => {
                        trackExplore({ dataProductName: asset?.dataProductName })
                        setTimeout(() => {
                          navigate(convertExploreUrlToWorkspace(exploreUrl))
                        }, 10)
                      }}
                      iconOnly={false}
                      icon={
                        accessControl?.hasAccess ? (
                          <IconExternalLink size={18} />
                        ) : (
                          <IconLock size={18} />
                        )
                      }
                      className="flex-1 justify-center p-1"
                      data-testid="asset-sidebar-explore-btn"
                    />
                  </div>
                )}
              </div>
            ) : null}
            {!showButtons && !accessControl?.hasAccess ? (
              <div className="flex space-y-2 mt-1">
                <Button
                  variant={accessControl?.isPending ? 'flat' : 'fill'}
                  disabled={accessControl?.isPending || accessControl?.isLoading}
                  label={
                    accessControl?.isLoading
                      ? 'Loading ...'
                      : accessControl?.isPending
                      ? 'Pending Request'
                      : 'Request access'
                  }
                  color={accessControl?.isPending ? 'warning' : 'primary'}
                  className={`flex-1 justify-center p-1${
                    accessControl?.isPending
                      ? ' bg-theme-pending-bg text-theme-pending-text border border-theme-pending-border'
                      : ''
                  }`}
                  icon={accessControl?.isPending ? <IconLock size={18} /> : ''}
                  onClick={() => requestAccess && requestAccess()}
                  iconOnly={false}
                  data-testid="DMRequestAccessBtn"
                />
              </div>
            ) : (
              ''
            )}

            {/* NEW DATA QUALITY MESSAGE */}
            {!showButtons ? (
              <div className={`theme-surface-muted p-1 rounded-[5px] shadow-md my-1`}>
                <div className="mr-2 theme-text-heading font-semibold text-lg mb-[4px]">
                  Data Quality Assurance
                </div>
                <div className="mr-2 theme-text-subheading ">
                  {dataQualityStatement?.length
                    ? dataQualityStatement
                    : `Data Quality information to be provided.`}
                </div>
              </div>
            ) : (
              <></>
            )}
          </CardBody>

          {dataScore && <DataQualityScore score={dataScore} />}
          {aiScore && <AIReadinessGauge score={aiScore} forceRerender={gaugeKey} />}
          {!showButtons && isUAT && <QuickActions />}
        </>
      )}
    </Card>
  )
}

export default AssetCard
